import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Link } from "react-router-dom"
import { motion, AnimatePresence } from "motion/react"

const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

const STORAGE_KEY = "cookieConsentAccepted"
/* Срок действия согласия — 12 месяцев (стандарт индустрии: 6–13 мес.),
   потом спрашиваем заново. Свёрнутый без принятия баннер не сохраняется —
   он сам напомнит о себе в следующую сессию */
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000

function readConsentTimestamp(): number | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        if (raw === "1") {
            // старый формат без даты — считаем принятым с этого момента
            const now = Date.now()
            localStorage.setItem(STORAGE_KEY, String(now))
            return now
        }
        const ts = Number(raw)
        return Number.isFinite(ts) ? ts : null
    } catch {
        // приватный режим без localStorage — не показываем баннер каждый раз
        return Date.now()
    }
}

function isAccepted() {
    const ts = readConsentTimestamp()
    return ts !== null && Date.now() - ts < CONSENT_TTL_MS
}

export default function CookieNotice() {
    const [accepted, setAccepted] = useState(isAccepted)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        if (accepted) return
        // Появляемся с небольшой задержкой, чтобы не мешать первому впечатлению от страницы
        const timer = setTimeout(() => setVisible(true), 1200)
        return () => clearTimeout(timer)
    }, [accepted])

    const accept = () => {
        try {
            localStorage.setItem(STORAGE_KEY, String(Date.now()))
        } catch {
            /* приватный режим — просто скрываем до следующего визита */
        }
        setAccepted(true)
        setVisible(false)
    }

    return createPortal(
        <>
            <AnimatePresence>
                {visible && (
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 24 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="fixed left-1/2 z-[80] w-[calc(100%-24px)] max-w-[560px] -translate-x-1/2 rounded-[20px] p-4 shadow-[0_12px_40px_rgba(0,0,0,0.45)] bottom-[calc(max(12px,var(--safe-bottom))+72px)] md:bottom-6 md:p-5"
                        style={{
                            background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)",
                            border: "1.5px solid rgba(255,255,255,0.12)",
                        }}
                        role="dialog"
                        aria-label="Использование файлов cookie">
                        {/* Свернуть в «печеньку» (mobile) — не отказ, просто убрать с глаз */}
                        <button
                            type="button"
                            onClick={() => setVisible(false)}
                            aria-label="Свернуть уведомление"
                            className="absolute top-2 right-2 flex size-7 cursor-pointer items-center justify-center rounded-full text-white/45 transition-colors hover:text-white md:hidden">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                                <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </button>
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-5">
                            <p
                                className="pr-6 text-[13px] leading-relaxed text-white/75 md:pr-0"
                                style={{ fontFamily: BODY, fontWeight: 500 }}>
                                Мы используем файлы cookie, чтобы сайт работал корректно и был удобнее.
                                Оставаясь на сайте, вы соглашаетесь с их использованием.{" "}
                                <Link
                                    to="/privacy#cookies"
                                    className="whitespace-nowrap text-[#349be6] underline underline-offset-2 transition-colors hover:text-white">
                                    Как мы используем cookie
                                </Link>
                            </p>
                            <button
                                type="button"
                                onClick={accept}
                                className="shrink-0 cursor-pointer self-start rounded-full px-6 py-2.5 text-[13px] text-white transition-opacity hover:opacity-90 md:self-auto"
                                style={{ background: "#e4206b", fontFamily: MONO, fontWeight: 700 }}>
                                Принять
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Плавающая «печенька» у правого края (mobile), пока условия не приняты */}
            <AnimatePresence>
                {!accepted && !visible && (
                    <motion.button
                        type="button"
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 24 }}
                        transition={{ type: "spring", stiffness: 300, damping: 28 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setVisible(true)}
                        aria-label="Об использовании файлов cookie"
                        className="fixed right-0 z-[80] flex h-11 w-12 cursor-pointer items-center justify-center rounded-l-full pr-1 text-[20px] shadow-[0_6px_20px_rgba(0,0,0,0.4)] bottom-[calc(max(12px,var(--safe-bottom))+140px)] md:hidden"
                        style={{
                            background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)",
                            border: "1.5px solid rgba(255,255,255,0.12)",
                            borderRight: "none",
                        }}>
                        🍪
                    </motion.button>
                )}
            </AnimatePresence>
        </>,
        document.body,
    )
}
